import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { addressLine } from '../lib/format.js'
import { useSession } from '../lib/session.js'
import { errorMessage, useTRPC } from '../lib/trpc.js'
import {
  AddressField,
  addressIsUsable,
  emptyAddress,
  type AddressDraft,
} from './AddressField.js'
import { Button, Field, Input, Spinner } from './ui.js'

/**
 * Which property the work is at — chosen from the client's, or created here.
 *
 * The same motion as `ClientPicker`, for the same reason: the address was the
 * one thing a new job could not be given without leaving the form, so it was
 * given "No particular site" instead, and 90,696 imported jobs already prove
 * where that ends. A site is a real record with a geocode behind it, so
 * creating one is a small form rather than a text box — and the form is here.
 *
 * Picking nothing stays possible. A job phoned in before anybody has the
 * address is a real thing that happens, and refusing to write it down is not
 * an improvement. It is stated rather than defaulted to.
 */

export function SitePicker({
  id = 'site-picker',
  clientId,
  value,
  onChange,
  allowNone = true,
}: {
  id?: string
  clientId: string | null
  /** The chosen site's id. Empty string means no particular site. */
  value: string
  onChange: (siteId: string) => void
  allowNone?: boolean
}) {
  const trpc = useTRPC()
  const { can } = useSession()

  const [creating, setCreating] = useState(false)
  const [decided, setDecided] = useState(false)
  const [filter, setFilter] = useState('')

  const sites = useQuery({
    ...trpc.clients.sitesForClient.queryOptions({ clientId: clientId ?? '' }),
    enabled: Boolean(clientId),
  })

  if (!clientId) {
    return (
      <Field label="Site" htmlFor={id}>
        <p className="text-xs text-ink-400" id={id}>
          Pick a client first — properties belong to one.
        </p>
      </Field>
    )
  }

  const rows = sites.data ?? []
  const chosen = rows.find((site) => site.id === value)

  // "Picked" covers picking nothing on purpose, which is why it is a separate
  // flag: an empty id cannot tell "not answered yet" from "answered no".
  if (decided || chosen) {
    return (
      <Field label="Site" htmlFor={id}>
        <div className="flex items-center gap-2.5 rounded-md bg-ink-50 px-2.5 py-2 ring-1 ring-inset ring-ink-200">
          <span className="min-w-0 flex-1">
            {chosen ? (
              <>
                <span className="block truncate text-sm font-semibold text-ink-900">
                  {chosen.name ?? addressLine(chosen.address)}
                </span>
                <span className="block truncate text-xs text-ink-500">
                  {chosen.name ? addressLine(chosen.address) : `Site ${chosen.number}`}
                </span>
              </>
            ) : (
              <>
                <span className="block text-sm font-semibold text-ink-900">
                  No particular site
                </span>
                <span className="block text-xs text-ink-500">
                  No address on this job. It will not appear on the map.
                </span>
              </>
            )}
          </span>
          <Button
            size="sm"
            type="button"
            onClick={() => {
              setDecided(false)
              setCreating(false)
            }}
          >
            Change
          </Button>
        </div>
      </Field>
    )
  }

  if (creating) {
    return (
      <NewSitePanel
        clientId={clientId}
        onCancel={() => setCreating(false)}
        onCreated={(siteId) => {
          setCreating(false)
          setDecided(true)
          onChange(siteId)
        }}
      />
    )
  }

  // A residential client has one property and a strata manager has ninety, so
  // the filter appears only where scanning stops working.
  const filterable = rows.length > 6
  const needle = filter.trim().toLowerCase()
  const visible =
    filterable && needle
      ? rows.filter((site) =>
          `${site.name ?? ''} ${addressLine(site.address)}`.toLowerCase().includes(needle),
        )
      : rows

  return (
    <Field label="Site" htmlFor={id} hint="Where the work is.">
      {filterable ? (
        <Input
          id={id}
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault()
          }}
          placeholder="Filter this client’s properties…"
        />
      ) : null}

      {sites.isLoading ? (
        <p className="flex items-center gap-1.5 text-xs text-ink-400">
          <Spinner tone="brand" className="size-3" /> Loading properties…
        </p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-ink-500">This client has no properties on file yet.</p>
      ) : visible.length === 0 ? (
        <p className="text-xs text-ink-500">No property matches that.</p>
      ) : (
        <ul
          className="max-h-44 overflow-y-auto rounded-md ring-1 ring-inset ring-ink-200"
          id={filterable ? undefined : id}
        >
          {visible.map((site) => (
            <li key={site.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(site.id)
                  setDecided(true)
                }}
                className="flex min-h-[var(--control-sm)] w-full flex-col justify-center px-2.5 py-1.5 text-left transition-colors hover:bg-ink-50"
              >
                <span className="truncate text-[13px] font-medium text-ink-900">
                  {site.name ?? addressLine(site.address)}
                </span>
                {site.name ? (
                  <span className="truncate text-[11.5px] text-ink-500">
                    {addressLine(site.address)}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {can('CLIENT_EDIT') ? (
          <Button size="sm" type="button" onClick={() => setCreating(true)}>
            New address
          </Button>
        ) : null}
        {allowNone ? (
          <button
            type="button"
            onClick={() => {
              onChange('')
              setDecided(true)
            }}
            className="text-xs text-ink-500 hover:text-ink-800"
          >
            No particular site
          </button>
        ) : null}
      </div>
    </Field>
  )
}

/**
 * A new property for this client.
 *
 * A `div` and not a `form`, like `NewClientPanel`: this is mounted inside the
 * New job form and inside the job card, and a nested form is invalid markup
 * whose submit escapes to the outer one.
 *
 * The site name is optional and deliberately second. Most properties are just
 * an address; a name is for the customer with three of them and a way of
 * referring to each — "Warehouse 3", not "Unit 14".
 */
export function NewSitePanel({
  clientId,
  onCancel,
  onCreated,
}: {
  clientId: string
  onCancel: () => void
  onCreated: (siteId: string) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const [address, setAddress] = useState<AddressDraft>(emptyAddress)
  const [name, setName] = useState('')

  const create = useMutation(
    trpc.clients.createSite.mutationOptions({
      onSuccess: (site) => {
        void queryClient.invalidateQueries(trpc.clients.pathFilter())
        onCreated(site.id)
      },
    }),
  )

  const ready = addressIsUsable(address)

  function submit() {
    if (!ready || create.isPending) return

    create.mutate({
      clientId,
      name: name.trim() || null,
      address: {
        ...address,
        // Kept verbatim when it came from the lookup; composed when it was
        // typed, so a site created here reads the same as an imported one
        // wherever `raw` is the fallback.
        raw: address.raw ?? addressLine(address),
      },
    })
  }

  return (
    <div
      className="flex flex-col gap-3 rounded-md bg-ink-50 p-2.5 ring-1 ring-inset ring-ink-200"
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return
        event.preventDefault()
        submit()
      }}
    >
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-semibold text-ink-900">New address</h3>
        <p className="min-w-0 flex-1 truncate text-xs text-ink-500">
          A property this client owns. It keeps its own history and access notes.
        </p>
        <Button size="sm" type="button" onClick={onCancel}>
          Back
        </Button>
      </div>

      <AddressField id="new-site-address" autoFocus value={address} onChange={setAddress} />

      <Field
        label="Name it (optional)"
        htmlFor="new-site-name"
        hint="Only worth it when this client has several."
      >
        <Input
          id="new-site-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Warehouse 3"
        />
      </Field>

      {create.error ? (
        <p className="text-xs text-red-600" role="alert">
          {errorMessage(create.error)}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-xs text-ink-500">
          {ready ? addressLine(address) : 'A street and a suburb are the minimum.'}
        </p>
        <Button
          type="button"
          variant="primary"
          size="sm"
          loading={create.isPending}
          disabled={!ready}
          onClick={submit}
        >
          Save address
        </Button>
      </div>
    </div>
  )
}
